import { ArrowLeft, SkipForward, Send } from "lucide-react";
import DrawingBoard from "./DrawingBoard";
import { useProfileContext } from "../../../context/ProfileContext";
import WordImage from "./WordImage";
import TaskProgress, { type TaskStatus } from "./TaskProgress";

type Props = {
  drawingRef: React.RefObject<any>;
  onBack: () => void;
  onSubmit: () => void;
  onSkip: () => void;
  isUploading: boolean;
  uploadStatus: string;
  taskStatus: TaskStatus;
  onHome: () => void;
};

const DrawingPage = ({
  drawingRef,
  onBack,
  onSubmit,
  onSkip,
  isUploading,
  uploadStatus,
  taskStatus,
  onHome,
}: Props) => {
  const { currentWord } = useProfileContext();

  return (
    <div className="h-dvh bg-gradient-to-br from-purple-200 via-pink-100 to-yellow-100 p-4 flex items-start justify-center overflow-y-auto">
      <div className="bg-white rounded-3xl shadow-xl p-5 max-w-xl w-full my-auto">
        <TaskProgress taskStatus={taskStatus} />

        {/* Title */}
        <h1 className="text-3xl font-bold text-purple-700 text-center mb-3">
          Draw this Word
        </h1>

        {/* Word Reference */}
        <div className="flex flex-col items-center gap-1 mb-3">
          <div className="bg-white rounded-2xl p-3 shadow-sm border border-purple-100">
            <WordImage />
          </div>
          <h2 className="text-lg font-bold text-purple-700">
            {currentWord?.english}
          </h2>
        </div>

        {/* Drawing Canvas */}
        <div className="mb-3">
          <DrawingBoard
            ref={drawingRef}
            key={currentWord?.english + "drawing"}
          />
        </div>

        {/* Upload Status */}
        {uploadStatus && (
          <div
            className={`mb-2 p-2 rounded-xl font-semibold text-center text-sm ${
              uploadStatus.includes("✅")
                ? "bg-green-100 text-green-700 border-2 border-green-300"
                : uploadStatus.includes("❌")
                ? "bg-red-100 text-red-700 border-2 border-red-300"
                : "bg-purple-100 text-purple-700 border-2 border-purple-300"
            }`}
          >
            {uploadStatus}
          </div>
        )}

        {/* Primary Action - Submit */}
        <button
          onClick={onSubmit}
          disabled={isUploading}
          className={`w-full bg-gradient-to-r from-pink-500 via-purple-500 to-blue-500 text-white font-bold py-2.5 px-4 rounded-2xl shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 mb-2 ${
            isUploading ? "opacity-70 cursor-not-allowed" : ""
          }`}
        >
          {isUploading ? (
            <>
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
              <span className="text-base">Checking your drawing...</span>
            </>
          ) : (
            <>
              <span className="text-base">Submit Drawing</span>
              <Send className="w-5 h-5" />
            </>
          )}
        </button>

        {/* Secondary Actions */}
        <div className="grid grid-cols-2 gap-2 mb-2">
          <button
            onClick={onBack}
            className="bg-white border-2 border-purple-300 text-purple-700 font-bold py-2 px-3 rounded-2xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>

          <button
            onClick={onSkip}
            className="bg-white border-2 border-purple-300 text-purple-700 font-bold py-2 px-3 rounded-2xl shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-200 flex items-center justify-center gap-2 text-sm"
          >
            Skip
            <SkipForward className="w-4 h-4" />
          </button>
        </div>

        <button
          onClick={onHome}
          className="w-full text-purple-600 hover:text-purple-700 text-sm font-semibold py-1 transition-all duration-200"
        >
          Return to Home
        </button>
      </div>
    </div>
  );
};

export default DrawingPage;
